import { useMemo } from 'react'
import { StatusRegistry } from '@shared/lib/filters/status-registry'
import type { FilterOption } from '@shared/types/filters'

const STATUS_COLOR_MAP: Record<string, { bgColor: string; textColor: string }> = {
  success: { bgColor: 'bg-status-success-bg', textColor: 'text-status-success-text' },
  error: { bgColor: 'bg-status-error-bg', textColor: 'text-status-error-text' },
  warning: { bgColor: 'bg-status-warning-bg', textColor: 'text-status-warning-text' },
  info: { bgColor: 'bg-status-info-bg', textColor: 'text-status-info-text' },
  pending: { bgColor: 'bg-status-pending-bg', textColor: 'text-status-pending-text' },
  neutral: { bgColor: 'bg-muted', textColor: 'text-muted-foreground' },
}

export function useEntityStatus<T extends { status?: string }>(
  entityType: string,
  entity: T | undefined
) {
  const statusInfo = useMemo(() => {
    const status = entity?.status || 'unknown'

    if (!StatusRegistry.hasEntity(entityType)) {
      return null
    }

    const statusOptions = StatusRegistry.getOptions(entityType) as FilterOption[]
    const statusOption = statusOptions.find(option => option.value === status)

    const color = statusOption?.color || 'neutral'
    const label = statusOption?.label || status

    const colors = STATUS_COLOR_MAP[color] || STATUS_COLOR_MAP.neutral

    return {
      status,
      styling: {
        bgColor: colors.bgColor,
        textColor: colors.textColor,
        label
      }
    }
  }, [entityType, entity?.status])

  return statusInfo
}
